import React, { useState, useEffect } from 'react';
import { Box, Button, Chip, Grid, IconButton, Link, Tooltip, Typography } from '@mui/material';
import { GridToolbarContainer, GridToolbarColumnsButton, GridToolbarFilterButton, GridToolbarDensitySelector, GridToolbarQuickFilter } from '@mui/x-data-grid';
import Add from '@mui/icons-material/Add';
import Remove from '@mui/icons-material/Remove';
import ArrowBack from '@mui/icons-material/ArrowBack';
import Close from '@mui/icons-material/Close';
import Check from '@mui/icons-material/Check';
import RemoveRedEye from '@mui/icons-material/RemoveRedEye';
import WorkOutline from '@mui/icons-material/WorkOutlined';
import { useParams, useHistory, Link as RouterLink } from 'react-router-dom';
import moment from 'moment';
import { withSnackbar } from '../../utils/SnackbarHOC';
import { getAllCandidatsForMission } from '../../services/CandidatService';
import { validateProcess, sendEmailCandidat } from '../../services/ProcessService';
import { addAppointment } from '../../services/AppointmentService';
import { getDetails } from '../../services/MissionService';
import { getSenioriteFromDate, updateObjectStateValue, useQuery } from '../../utils/FunctionHelpers';
import { displaySwipeableDrawerSummaryCandidat } from '../../redux/SwipeableDrawer/actions/swipeableDrawerActions';
import { useDispatch } from 'react-redux';
import AppointmentModal from '../Candidat/VisualiserCandidat/Tabs/Modals/CandidatProcess/AppointmentModal';
import EmailModal from '../../components/EmailModal/EmailModal';
import ShowAddCvCandidat from '../Candidat/VisualiserCandidat/ShowAddCvCandidat';
import Loader from '../../components/Loader/Loader';
import { TABLE } from '../../enums/tablesEnum';
import { removeLineBreak } from '../../utils/EditorUtil';
import { commonGridSx, gridLocaleText } from '../../utils/gridStyles';
import { DataGrid } from '../../components/SortableDataGrid/SortableDataGrid';

const formatDate = (val) => val ? moment(new Date(val)).format('DD/MM/YYYY') : '—';

const getStepLabel = (row) => {
  const expectedStatus = ['Non validée', 'Embauche', 'Integration', 'REFUSÉ PAR CLIENT', 'REFUSÉ PAR CANDIDAT', 'Refusé par client', 'Refusé par candidat', 'Refusé par Go&Dev', 'No show'];
  if (expectedStatus.includes(row.step)) return row.step;
  if (row.rvClosing) return 'RV Closing';
  if (row.rvClient) return 'RV CLIENT';
  if (row.sent) return 'ENVOI CLIENT';
  if (row.testInterne) return 'TEST INTERNE';
  if (row.dateAccrochage) return 'Préqualif RH';
  return 'Pré-selection';
};

const stepColors = {
  'Non validée': { bg: '#fef2f2', color: '#991b1b', border: '#fecaca' },
  'Embauche': { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
  'Integration': { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
  'REFUSÉ PAR CLIENT': { bg: '#fef2f2', color: '#991b1b', border: '#fecaca' },
  'RV Closing': { bg: '#fdf4ff', color: '#86198f', border: '#f0abfc' },
  'RV CLIENT': { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
  'ENVOI CLIENT': { bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' },
  'TEST INTERNE': { bg: '#fefce8', color: '#854d0e', border: '#fde047' },
  'Préqualif RH': { bg: '#f0f9ff', color: '#075985', border: '#bae6fd' },
  'Pré-selection': { bg: '#f8fafc', color: '#64748b', border: '#e2e8f0' },
};

const CandidatsListInMission = ({ snackbarShowMessage }) => {
  const [reloadData, setReloadData] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [candidats, setCandidats] = useState([]);
  const { idMission } = useParams();
  const [candidatsInactifs, setCandidatsInactifs] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();
  const [missionData, setMissionData] = useState({ mission: {}, isLoading: true });
  const query = useQuery();
  const [openModal, setOpenModal] = useState(false);
  const [messageType, setMessageType] = useState();
  const [prenom, setPrenom] = useState();
  const [email, setEmail] = useState();
  const [idCandidat, setIdCandidat] = useState();

  const filterAppointmentsByStatut = (appointments, matchString) =>
    appointments?.find(({ status }) => new RegExp(matchString).test(status));

  const storeNavigationState = (candidatId, tabIndex) => {
    const candidatsIds = candidats.map((c) => c.id);
    localStorage.setItem('viewCandidatState', JSON.stringify({ tabIndex, candidatsInList: candidatsIds, candidatIndex: candidatsIds.indexOf(candidatId), numbersTotalOfSelections: candidats.length }));
  };

  const viewCandidat = (candidatId, tabIndex) => {
    const candidatsIds = candidats.map((c) => c.id);
    storeNavigationState(candidatId, tabIndex);
    history.push({ pathname: `/candidats/${candidatId}/view`, state: { tabIndex, candidatsInList: candidatsIds, candidatIndex: candidatsIds.indexOf(candidatId), numbersTotalOfSelections: candidats.length } });
  };

  // Plain click: in-app navigation (keeps Précédent / Suivant). Ctrl/Cmd/Shift-click
  // and middle-click are left to the browser so the profile opens in a new tab.
  const openCandidat = (e, candidatId) => {
    if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    viewCandidat(candidatId, 0);
  };

  const candidatLinkSx = {
    textDecoration: 'none', fontSize: '1rem', cursor: 'pointer', borderRadius: '4px', transition: 'color 0.12s',
    '&:hover': { color: '#0556a4', textDecoration: 'underline', textUnderlineOffset: '3px' },
  };

  React.useEffect(() => {
    let active = true;
    if (idMission === 'undefined') { history.push({ pathname: '/missions' }); return; }
    (async () => {
      updateObjectStateValue(setMissionData, { isLoading: true, mission: {} });
      try {
        const [missionResponse, candidatsResponse] = await Promise.all([
          getDetails(idMission),
          getAllCandidatsForMission(idMission, query.get('isSelected'), candidatsInactifs),
        ]);
        if (!active) return;
        updateObjectStateValue(setMissionData, { isLoading: false, mission: missionResponse.data });
        setCandidats(candidatsResponse.data.candidats || []);
      } catch {
        snackbarShowMessage('Erreur de chargement de données.', 'error', 4000);
      } finally {
        setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [reloadData]);

  const getData = async () => {
    try {
      setCandidatsInactifs(!candidatsInactifs);
      setCandidats([]);
      setLoadingData(true);
      const candidatsResponse = await getAllCandidatsForMission(idMission, query.get('isSelected'), !candidatsInactifs);
      setCandidats(candidatsResponse.data.candidats || []);
    } catch {
      snackbarShowMessage('Erreur de chargement de données.', 'error', 4000);
    } finally {
      setLoadingData(false);
    }
  };

  const handleValidateProcess = async (processID) => {
    try {
      snackbarShowMessage('Validation du process en cours...', 'warning');
      await validateProcess(processID);
      snackbarShowMessage('Process validé avec succès !');
      setReloadData(!reloadData);
    } catch (error) {
      snackbarShowMessage(error.response, 'error', 4000);
    }
  };

  const handleNotValidateProcess = async (processID) => {
    try {
      snackbarShowMessage('Non-validation du process en cours...', 'warning');
      const payload = { date: null, status: 'Non validée', commentaire: removeLineBreak(''), dateIntegration: null, candidat: '', selected: [] };
      await addAppointment(processID, payload);
      snackbarShowMessage('Process marqué comme non validé.');
    } catch (error) {
      snackbarShowMessage(error.response?.data?.error?.detail || 'Une erreur est survenue');
    }
    setReloadData(!reloadData);
  };

  const getLastNameOnly = (row = {}) => {
    const prenom = String(row.prenom || '').trim();
    const nom = String(row.nom || '').trim();
    if (prenom && nom.toLowerCase().startsWith(prenom.toLowerCase())) return nom.slice(prenom.length).trim() || nom;
    return nom || '—';
  };

  const isSelectedList = query.get('isSelected') === 'true';
  const listLabel = isSelectedList ? 'CV en cours' : 'CV pressentis';
  const mission = missionData.mission || {};

  const CustomToolbar = () => (
    <GridToolbarContainer sx={{ px: 2, py: 1.25, borderBottom: '1px solid #f1f5f9', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
      <Button
        startIcon={!candidatsInactifs ? <Add sx={{ fontSize: 16 }} /> : <Remove sx={{ fontSize: 16 }} />}
        onClick={getData}
        sx={{ textTransform: 'none', fontWeight: 500, fontSize: '1rem', color: '#03407b', border: '1px solid #aed6f1', borderRadius: 2, px: 2, py: 0.75, background: '#ebf4fb', '&:hover': { background: '#d6eaf8' } }}>
        {!candidatsInactifs ? 'Candidats inactifs' : 'Masquer inactifs'}
      </Button>
      <GridToolbarQuickFilter
        sx={{
          ml: 'auto',
          '& .MuiInputBase-root': { borderRadius: 2, fontSize: '1rem', background: '#f8fafc', px: 1.5, height: 36 },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
          '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#7fb3d3' },
          '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#03407b' },
        }}
        placeholder="Rechercher..."
        debounceMs={400}
      />
      <GridToolbarColumnsButton sx={{ textTransform: 'none', fontSize: '1rem', color: '#64748b', borderRadius: 2 }} />
      <GridToolbarFilterButton sx={{ textTransform: 'none', fontSize: '1rem', color: '#64748b', borderRadius: 2 }} />
      <GridToolbarDensitySelector sx={{ textTransform: 'none', fontSize: '1rem', color: '#64748b', borderRadius: 2 }} />
    </GridToolbarContainer>
  );

  const columns = [
    {
      field: 'numCandidat', headerName: 'N°', width: 80,
      renderCell: (p) => {
        const files = p.row.uploadedFiles || [];
        const candidatName = `${p.row.prenom || ''} ${getLastNameOnly(p.row)}`.trim();
        return <ShowAddCvCandidat snackbarShowMessage={snackbarShowMessage} files={files} fileName={candidatName}>{p.value}</ShowAddCvCandidat>;
      },
    },
    {
      field: 'nom', headerName: 'NOM', width: 130, valueGetter: (value, row) => getLastNameOnly(row),
      renderCell: (p) => (
        <Link component={RouterLink} to={`/candidats/${p.row.id}/view`}
          title="Ouvrir la fiche candidat"
          onContextMenu={() => storeNavigationState(p.row.id, 0)}
          onMouseDown={() => storeNavigationState(p.row.id, 0)}
          onClick={(e) => openCandidat(e, p.row.id)}
          sx={{ ...candidatLinkSx, color: '#0f172a', fontWeight: 600, textTransform: 'uppercase' }}>
          {getLastNameOnly(p.row)}
        </Link>
      ),
    },
    {
      field: 'prenom', headerName: 'PRÉNOM', width: 140,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Voir résumé">
            <IconButton size="small" onClick={() => dispatch(displaySwipeableDrawerSummaryCandidat({ show: true, id: p.row.id }))}
              sx={{ color: '#94a3b8', p: 0.25, '&:hover': { color: '#03407b', background: '#ebf4fb' }, borderRadius: 1, flexShrink: 0 }}>
              <RemoveRedEye sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
          <Link component={RouterLink} to={`/candidats/${p.row.id}/view`}
            title="Ouvrir la fiche candidat"
            onContextMenu={() => storeNavigationState(p.row.id, 0)}
            onMouseDown={() => storeNavigationState(p.row.id, 0)}
            onClick={(e) => openCandidat(e, p.row.id)}
            sx={{ ...candidatLinkSx, color: '#334155' }}>
            {p.value}
          </Link>
        </Box>
      ),
    },
    {
      field: 'step', headerName: 'STEP', width: 140, valueGetter: (value, row) => getStepLabel(row),
      renderCell: (p) => {
        const label = getStepLabel(p.row);
        const cfg = stepColors[label] || { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' };
        return <Chip label={label} size="small" sx={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, fontWeight: 600, fontSize: '0.7rem', height: 22 }} />;
      },
    },
    {
      field: 'nom_societe', headerName: 'EMPLOYEUR', width: 150,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.value ?? '—'}</Typography>,
    },
    {
      field: 'dateDebutExperience', headerName: 'XP', width: 70, align: 'center', headerAlign: 'center',
      sortComparator: (a, b) => getSenioriteFromDate(a?.date) - getSenioriteFromDate(b?.date),
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#475569' }}>{p.value ? getSenioriteFromDate(p.value?.date) : 'N/A'}</Typography>,
    },
    {
      field: 'fonction', headerName: 'FONCTION', width: 220,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.value || '—'}</Typography>,
    },
    {
      field: 'localisation', headerName: 'LOCALISATION', width: 130,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#475569' }}>{p.value ?? '—'}</Typography>,
    },
    {
      field: 'diplome', headerName: 'DIPLÔME', width: 130,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#475569' }}>{p.value ?? '—'}</Typography>,
    },
    {
      field: 'consultant', headerName: 'TALENT MANAGER', width: 150,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#475569' }}>{p.value ?? '—'}</Typography>,
    },
    {
      field: 'sourceur', headerName: 'SOURCEUR', width: 130,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#475569' }}>{p.value ?? '—'}</Typography>,
    },
    {
      field: 'dateAccrochage', headerName: 'PRÉQUALIF RH', width: 120,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#64748b' }}>{formatDate(p.value)}</Typography>,
    },
    {
      field: 'testInterne', headerName: 'TEST INTERNE', width: 120,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#64748b' }}>{formatDate(p.value)}</Typography>,
    },
    {
      field: 'testeur', headerName: 'TESTEUR', width: 130,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#475569' }}>{p.value ?? '—'}</Typography>,
    },
    {
      field: 'sent', headerName: 'ENVOI CLIENT', width: 120,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#64748b' }}>{formatDate(p.value)}</Typography>,
    },
    {
      field: 'rvClient', headerName: 'RV CLIENT', width: 110,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#64748b' }}>{formatDate(p.value)}</Typography>,
    },
    {
      field: 'rvClosing', headerName: 'RV CLOSING', width: 115,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#64748b' }}>{formatDate(p.value)}</Typography>,
    },
    {
      field: 'dateEmbauche', headerName: 'EMBAUCHE', width: 110,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#64748b' }}>{formatDate(p.value)}</Typography>,
    },
    {
      field: 'dateIntegration', headerName: 'INTÉGRATION', width: 120,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#64748b' }}>{formatDate(p.value)}</Typography>,
    },
    {
      field: 'datePreSelection', headerName: 'PRÉ-SÉLECTION', width: 130,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#64748b' }}>{p.value?.date ? formatDate(new Date(p.value.date)) : '—'}</Typography>,
    },
    ...(query.get('isSelected') === 'true' ? [{
      field: 'selection_actions', headerName: 'SÉLECTION', width: 110, sortable: false, filterable: false,
      renderCell: (p) => {
        const disabled = !!(p.row.dateAccrochage || p.row.step === 'Non validée');
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="Non Valider">
              <span><IconButton size="small" disabled={disabled} onClick={() => handleNotValidateProcess(p.row.idProcess)}
                sx={{ color: disabled ? '#d1d5db' : '#ef4444', p: 0.75, borderRadius: '8px', background: disabled ? 'transparent' : '#fef2f2' }}>
                <Close sx={{ fontSize: 16 }} />
              </IconButton></span>
            </Tooltip>
            <Tooltip title="Valider">
              <span><IconButton size="small" disabled={disabled} onClick={() => handleValidateProcess(p.row.idProcess)}
                sx={{ color: disabled ? '#d1d5db' : '#16a34a', p: 0.75, borderRadius: '8px', background: disabled ? 'transparent' : '#f0fdf4' }}>
                <Check sx={{ fontSize: 16 }} />
              </IconButton></span>
            </Tooltip>
          </Box>
        );
      },
    }] : []),
    ...(query.get('isSelected') === 'true' ? [{
      field: 'actions', headerName: 'ISSUE', width: 130, sortable: false, filterable: false,
      renderCell: (p) => (
        <AppointmentModal
          processId={p.row.idProcess}
          reloadData={reloadData} setReloadData={setReloadData}
          snackbarShowMessage={snackbarShowMessage}
          handleOpenModal={() => setOpenModal(true)}
          setMessageType={setMessageType}
          setCandidatInfo={() => { setPrenom(p.row.prenom); setEmail(p.row.email); setIdCandidat(p.row.id); }}
        />
      ),
    }] : []),
  ];

  const extractDate = (appointments, statusRegex) => {
    const appt = filterAppointmentsByStatut(appointments, statusRegex);
    return appt ? moment(appt.date).toDate().getTime() : null;
  };

  const rows = (candidats || []).map((row) => ({
    ...row,
    id: row.id,
    sent: row.sent?.date != null ? moment(row.sent.date).toDate().getTime() : null,
    dateAccrochage: row.dateAccrochage?.date != null ? moment(row.dateAccrochage.date).toDate().getTime() : null,
    rvClient: row.rvClient?.date != null ? moment(row.rvClient.date).toDate().getTime() : null,
    // TEST INTERNE = date of the test technique (Process client), old rvTech as fallback
    testInterne: (row.testTechniqueAt?.date || row.rvTech?.date) ? moment(row.testTechniqueAt?.date || row.rvTech.date).toDate().getTime() : null,
    rvClosing: row.rvClosing?.date != null ? moment(row.rvClosing.date).toDate().getTime() : null,
  }));

  if (loading) return <Loader />;

  return (
    <Box sx={{ px: 2.5, pb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
        <Tooltip title="Retourner vers la liste">
          <IconButton onClick={() => history.goBack()} size="small" sx={{ color: '#64748b' }}>
            <ArrowBack sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
        <Box sx={{ width: 38, height: 38, borderRadius: 2, background: '#ebf4fb', color: '#03407b', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <WorkOutline sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {listLabel} —{' '}
            <Link component={RouterLink} to={`/societe/${mission.societeId}/missions/${mission.uuid}/view`}
              sx={{ color: '#03407b', fontWeight: 800, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
              {mission.nom_mission || 'Mission'}
            </Link>
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: '#64748b' }}>
            {[
              mission.numMission && `N°${mission.numMission}`,
              mission.nom_societe,
              mission.nb_postes && `${mission.nb_postes} poste${mission.nb_postes > 1 ? 's' : ''}`,
              mission.datecreation && `Créée le ${moment(mission.datecreation.date).format('DD/MM/YYYY')}`,
            ].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
        <Chip
          label={`${candidats.length} candidat${candidats.length > 1 ? 's' : ''}${candidatsInactifs ? ' (inactifs inclus)' : ''}`}
          sx={{ background: isSelectedList ? '#f0fdf4' : '#f1f5f9', color: isSelectedList ? '#16a34a' : '#475569', border: `1px solid ${isSelectedList ? '#bbf7d0' : '#e2e8f0'}`, fontWeight: 700 }}
        />
      </Box>
      <Box sx={{ borderRadius: 3, border: '1px solid #e2e8f0', height: 'calc(100vh - 200px)', minHeight: 400, background: '#fff', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.04)' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loadingData}
          pageSizeOptions={[50, 100]}
          disableRowSelectionOnClick
          showToolbar
          slots={{ toolbar: CustomToolbar }}
          slotProps={{ loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' } }}
          localeText={gridLocaleText}
          sx={commonGridSx}
        />
      </Box>
      <EmailModal
        loadData={reloadData} setLoadData={setReloadData} showButton={false}
        prenom={prenom} emailTarget={email} serviceEmail={sendEmailCandidat}
        id={idCandidat} category={TABLE.CANDIDAT}
        openModal={openModal} setOpenModal={setOpenModal}
        handleOpenModal={() => setOpenModal(true)} messageType={messageType}
      />
    </Box>
  );
};

export default withSnackbar(CandidatsListInMission);

